import React, { Component, ComponentProps, FC, PropsWithChildren } from 'react';
import { connect } from 'react-redux';

import { RootState } from 'MyTypes';
import { intersection, isEmpty, sortBy } from 'lodash/fp';

import {
    Alert,
    Button,
    ButtonDropdown,
    FlexColumn,
    FlexRow,
    Form,
    FormGroup,
    Link,
} from '@portal/common/components';
import {
    AGE_GROUP_ID_KEY,
    ANTIBIOTIC_CLASS_ID_KEY,
    CAUSE_ID_KEY,
    COUNTERFACTUAL_ID_KEY,
    DAY_KEY,
    EDUCATION_ID_KEY,
    FORECAST_SCENARIO_ID_KEY,
    GENDER_ID_KEY,
    INFECTIOUS_SYNDROME_ID_KEY,
    LOCATION_ID_KEY,
    MEASURE_ID_KEY,
    METRIC_ID_KEY,
    PATHOGEN_ID_KEY,
    PAYER_ID_KEY,
    RACE_ID_KEY,
    RISK_EXPOSURE_ID_KEY,
    ROUND_ID_KEY,
    TOC_ID_KEY,
    YEAR_KEY,
} from '@portal/common/models/data-key';
import { getDataTypePrimaryEntityKey } from '@portal/common/models/data-type';
import { withStorage } from '@portal/common/services/local-storage';
import { styled } from '@portal/common/theme';
import { DataGranularity, DataKey, RefinementFilterConfig } from '@portal/common/types';
import { compose } from '@portal/common/utility/compose';

import _ from '../../locale';
import {
    addVisibleRefinementFilters,
    changeSelectedDataType,
    dataSelectionValidation,
    resetDataTool,
} from '../../store/data-explorer/actions';
import {
    getDataToolConfig,
    getDataToolDataCollections,
    getSelectedConditions,
    getSelectedConditionsDataTypes,
    getSelectedConditionsDataTypesDefaultFilters,
    getSelectedConditionsDataTypesGranularity,
    getSelectedConditionsRefinedGranularity,
    getSelectedRefinementFiltersWithFallbackToDefaultFilters,
    getVisibleRefinementFilters,
    hasCdnRestrictions,
    hasErrorInRefinements,
    hasFiltersChangedSinceLastRequest,
    isDataLoading,
} from '../../store/data-explorer/selectors';
import { getSelectedDataCollectionForDataTool } from '../../store/user-settings/selectors';
import sortRefinementsOptions from '../../utility/sort-refinements-options';
import FiltersResetBtn from '../FiltersResetBtn';
import RunQueryButton from '../RunQueryButton';
import ToggleChartsVisibilityButton from '../ToggleChartsVisibilityButton';
import ExportSingleAgeEstimates from './ExportSingleAgeEstimates';
import RefinementFilter from './RefinementFilter';
import { DataExplorerLayoutContext } from './context';

export const REFINEMENT_OPTIONS = sortRefinementsOptions([
    INFECTIOUS_SYNDROME_ID_KEY,
    MEASURE_ID_KEY,
    METRIC_ID_KEY,
    GENDER_ID_KEY,
    AGE_GROUP_ID_KEY,
    CAUSE_ID_KEY,
    RISK_EXPOSURE_ID_KEY,
    LOCATION_ID_KEY,
    YEAR_KEY,
    DAY_KEY,
    ROUND_ID_KEY,
    FORECAST_SCENARIO_ID_KEY,
    RACE_ID_KEY,
    PATHOGEN_ID_KEY,
    ANTIBIOTIC_CLASS_ID_KEY,
    COUNTERFACTUAL_ID_KEY,
    EDUCATION_ID_KEY,
    PAYER_ID_KEY,
    TOC_ID_KEY,
]);

export const REFINEMENT_FILTERS_CONFIG: RefinementFilterConfig = {
    [INFECTIOUS_SYNDROME_ID_KEY]: {
        isMulti: true,
        isSearchable: true,
        isCollapsible: true,
        placeholder: _('search_infectious_syndrome_id'),
    },
    [MEASURE_ID_KEY]: {
        isMulti: true,
        isSearchable: true,
        isCollapsible: true,
        placeholder: _('search_measure_id'),
    },
    [CAUSE_ID_KEY]: {
        isSearchable: true,
        isCollapsible: true,
        isMulti: true,
        placeholder: _('search_cause_id'),
    },
    [LOCATION_ID_KEY]: {
        isSearchable: true,
        isMulti: true,
        isCollapsible: true,
        placeholder: _('search_location_id'),
    },
    [AGE_GROUP_ID_KEY]: {
        isMulti: true,
        isSearchable: true,
        isCollapsible: true,
        placeholder: _('search_age_group_id'),
    },
    [FORECAST_SCENARIO_ID_KEY]: {
        isMulti: true,
        isSearchable: true,
        isCollapsible: true,
        placeholder: _('search_forecast_scenario_id'),
    },
    [GENDER_ID_KEY]: {
        isMulti: true,
        isSearchable: true,
        isCollapsible: true,
        placeholder: _('search_gender_id'),
    },
    [METRIC_ID_KEY]: {
        isMulti: true,
        isSearchable: true,
        isCollapsible: true,
        placeholder: _('search_metric_id'),
    },
    [YEAR_KEY]: {
        isMulti: true,
        mode: 'slider',
        isSearchable: true,
        isCollapsible: true,
        placeholder: _('search_year'),
        undefinedWhenAllSelected: true,
    },
    [RISK_EXPOSURE_ID_KEY]: {
        isMulti: true,
        isSearchable: true,
        isCollapsible: true,
        placeholder: _('search_risk_exposure_id'),
    },
    [RACE_ID_KEY]: {
        isMulti: true,
        isSearchable: true,
        isCollapsible: true,
        placeholder: _('search_race_id'),
    },
    [PATHOGEN_ID_KEY]: {
        isMulti: true,
        isSearchable: true,
        isCollapsible: true,
        placeholder: _('search_pathogen_id'),
    },
    [ANTIBIOTIC_CLASS_ID_KEY]: {
        isMulti: true,
        isSearchable: true,
        isCollapsible: true,
        placeholder: _('search_antibiotic_class_id'),
    },
    [COUNTERFACTUAL_ID_KEY]: {
        isMulti: true,
        isSearchable: true,
        isCollapsible: true,
        placeholder: _('search_counterfactual_id'),
    },
    [EDUCATION_ID_KEY]: {
        isMulti: true,
        isSearchable: true,
        isCollapsible: true,
        placeholder: _('search_education_id'),
    },
    [TOC_ID_KEY]: {
        isMulti: true,
        isSearchable: true,
        isCollapsible: true,
        placeholder: _('search_toc_id'),
    },
    [PAYER_ID_KEY]: {
        isMulti: true,
        isSearchable: true,
        isCollapsible: true,
        placeholder: _('search_payer_id'),
    },
};

const mapStateToProps = (state: RootState) => ({
    dataCollections: getDataToolDataCollections(state),
    defaultFilters: getSelectedConditionsDataTypesDefaultFilters(state),
    selectedConditions: getSelectedConditions(state),
    selectedConditionsDataTypes: getSelectedConditionsDataTypes(state),
    selectedDataCollection: getSelectedDataCollectionForDataTool(state),
    selectedRefinementFilters: getSelectedRefinementFiltersWithFallbackToDefaultFilters(state),
    visibleRefinementFilters: getVisibleRefinementFilters(state),
    granularity: getSelectedConditionsDataTypesGranularity(state),
    refinedGranularity: getSelectedConditionsRefinedGranularity(state),
    hasCdnRestrictions: hasCdnRestrictions(state),
    dataToolConfig: getDataToolConfig(state),
    errorInRefinements: hasErrorInRefinements(state),
    filtersChangedSinceLastRequest: hasFiltersChangedSinceLastRequest(state),
    isLoadingData: isDataLoading(state),
    yearRefinementFilterMode: state.dataExplorer.yearRefinementFilterMode,
    dayRefinementFilterMode: state.dataExplorer.dayRefinementFilterMode,
    organization: state.organization,
});

const dispatchProps = {
    resetDataTool: resetDataTool,
    changeSelectedDataType: changeSelectedDataType,
    addVisibleRefinementFilters: addVisibleRefinementFilters,
    queryDataValidationRequest: dataSelectionValidation.request,
};

const ButtonWrapper = styled.div({
    width: 230,
});

const ACTIONS_COMPACT_SPACING = 8;
const ACTIONS_REGULAR_SPACING = 20;

const getActionsSpacing = (isCompact: boolean) =>
    isCompact ? ACTIONS_COMPACT_SPACING : ACTIONS_REGULAR_SPACING;

const ActionsRow = styled(FlexRow)<{ isCompact: boolean }>(({ theme, isCompact }) => ({
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: getActionsSpacing(isCompact),
    marginTop: getActionsSpacing(isCompact),
    [theme.media.laptop]: {
        justifyContent: 'flex-end',
    },
}));

const ActionsGroup = styled(FlexRow)<{ isCompact: boolean }>(({ isCompact }) => ({
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: getActionsSpacing(isCompact),
}));

const ToggleFormGroup = styled(FormGroup)(({ theme }) => ({
    minWidth: 160,
    margin: 0,
    order: -1,
    flexBasis: '100%',
    [theme.media.laptop]: {
        order: 0,
        flexBasis: 'auto',
        marginRight: 'auto',
    },
}));

const LinkToCDN: FC<PropsWithChildren<ComponentProps<typeof Link>>> = ({ children, ...props }) => (
    <Link href="#cdn" style={{ color: 'white', textDecoration: 'underline' }} {...props}>
        {children}
    </Link>
);

const StyledAlert = styled(Alert)(() => ({
    marginBottom: 15,
}));

const BASIC_MODE = 'basic';
const ADVANCED_MODE = 'advanced';

export type RefinementFiltersControlsOwnProps = {
    refinementFiltersConfig?: RefinementFilterConfig;
    areChartsVisible: boolean;
    toggleChartsVisibility: () => void;
    forceAdvancedMode?: boolean;
    hideActions?: boolean;
};

type Props = ReturnType<typeof mapStateToProps> &
    typeof dispatchProps &
    RefinementFiltersControlsOwnProps;

type State = {
    viewMode: typeof BASIC_MODE | typeof ADVANCED_MODE;
};

class RefinementFiltersControls extends Component<Props, State> {
    static contextType = DataExplorerLayoutContext;

    context!: React.ContextType<typeof DataExplorerLayoutContext>;

    static defaultProps = {
        refinementFiltersConfig: REFINEMENT_FILTERS_CONFIG,
    };

    state: State = {
        viewMode: BASIC_MODE,
    };

    static getDerivedStateFromProps = (props: Props, state: State) =>
        props.dataToolConfig &&
        !props.dataToolConfig.enableBasicRefinementFiltersMode &&
        state.viewMode !== ADVANCED_MODE
            ? { viewMode: ADVANCED_MODE }
            : null;

    reset = () => {
        const { resetDataTool, dataToolConfig } = this.props;
        this.setState({ viewMode: BASIC_MODE });
        resetDataTool(dataToolConfig);
    };

    // get available refinements based on given granularity and condition
    getAvailableRefinementsForRefinedGranularity = (granularity: DataGranularity) => {
        const { selectedConditions, selectedRefinementFilters, visibleRefinementFilters } =
            this.props;

        const conditionFilters = isEmpty(selectedConditions)
            ? {}
            : selectedConditions.reduce((acc, i) => {
                  const key = getDataTypePrimaryEntityKey(i.data_type);
                  const val = i.primary_entity_id;
                  if (acc[key]) {
                      acc[key] += `,${val}`;
                  } else {
                      acc[key] = val;
                  }
                  return acc;
              }, {});

        return (
            REFINEMENT_OPTIONS.filter((i) => Object.prototype.hasOwnProperty.call(granularity, i))
                // filter granularity with values more than 1 or that one value isn't selected
                .filter((i) => {
                    const filterGranularity = granularity[i];
                    const selectedValue = selectedRefinementFilters[i];
                    const isSelectedValuePartOfGranularity =
                        isEmpty(selectedValue) ||
                        !isEmpty(intersection(selectedValue, filterGranularity));
                    return (
                        filterGranularity != null &&
                        (filterGranularity.length > 1 ||
                            !isSelectedValuePartOfGranularity ||
                            visibleRefinementFilters.includes(i))
                    );
                })
                .filter((i) => !Object.prototype.hasOwnProperty.call(conditionFilters, i))
        );
    };

    handleAddRefinementClick = (filterKey) => {
        this.props.addVisibleRefinementFilters(filterKey);
    };

    toggleAdvancedMode = () => {
        this.setState({ viewMode: ADVANCED_MODE });
    };

    toggleBasicMode = () => {
        this.setState({ viewMode: BASIC_MODE });
    };

    /**
     * @todo: I have a doubt that it doesn't work and is useless
     */
    getHiddenRefinementFilters = (): DataKey[] =>
        this.props.dataToolConfig.hiddenRefinementFilters ?? [];

    /**
     * There are two modes simple and advanced
     * For advanced showing all that's present in granularity
     * In simple we are showing enabled filters from visibleRefinementFilters
     * visibleRefinementFilters is also changed by validateVisibleRefinementFilters
     */
    renderFiltersControls = () => {
        const { viewMode } = this.state;
        const {
            visibleRefinementFilters,
            refinedGranularity,
            granularity,
            selectedRefinementFilters,
            refinementFiltersConfig,
            yearRefinementFilterMode,
            dayRefinementFilterMode,
            dataToolConfig,
            forceAdvancedMode,
        } = this.props;
        const fallbackGranularity = refinedGranularity || granularity;

        const hiddenFilters = this.getHiddenRefinementFilters();
        const availableRefinementFilters = this.getAvailableRefinementsForRefinedGranularity(
            fallbackGranularity
        ).filter((key) => !hiddenFilters.includes(key));

        const createOnChange = (key) => () => {
            this.handleAddRefinementClick(key);
        };

        const isAdvancedMode = forceAdvancedMode || viewMode === ADVANCED_MODE;

        // decides which filters are displayed
        const filters = sortRefinementsOptions(
            isAdvancedMode ? availableRefinementFilters : visibleRefinementFilters
        );
        return filters.map((filterKey) => {
            const mode = {
                [YEAR_KEY]: yearRefinementFilterMode,
                [DAY_KEY]: dayRefinementFilterMode,
            }[filterKey];

            return (
                <React.Fragment key={filterKey}>
                    <RefinementFilter
                        refinementFiltersConfig={refinementFiltersConfig}
                        key={filterKey}
                        mode={mode}
                        granularityKey={filterKey}
                        availableRefinementFilters={availableRefinementFilters}
                        selectedValue={selectedRefinementFilters[filterKey]}
                        hideSelectAll={!!dataToolConfig.areRefinementFiltersSmartOptionsDisabled}
                        {...(isAdvancedMode && {
                            hideDelete: true,
                            onChange: createOnChange(filterKey),
                        })}
                    />
                    {filterKey === AGE_GROUP_ID_KEY && <ExportSingleAgeEstimates />}
                </React.Fragment>
            );
        });
    };

    renderActionButtons = () => {
        const { viewMode } = this.state;
        const { visibleRefinementFilters, granularity, refinedGranularity, dataToolConfig } =
            this.props;

        const resetBtn =
            dataToolConfig && !dataToolConfig.disableResetRefinementFilters ? (
                <FiltersResetBtn onClick={this.reset} />
            ) : null;

        if (dataToolConfig && !dataToolConfig.enableBasicRefinementFiltersMode) {
            return resetBtn;
        }

        const labelGetter = (i) => _('refinement_' + i);
        const fallbackGranularity = refinedGranularity || granularity;
        const availableRefinementFilters = sortBy(
            labelGetter,
            this.getAvailableRefinementsForRefinedGranularity(fallbackGranularity).filter(
                (i) => !visibleRefinementFilters.includes(i)
            )
        );

        switch (viewMode) {
            case BASIC_MODE:
                return (
                    <>
                        {!isEmpty(availableRefinementFilters) && (
                            <ButtonWrapper>
                                <ButtonDropdown
                                    id="add-refinement"
                                    dataTestid="add-refinement"
                                    options={availableRefinementFilters}
                                    optionLabelGetter={labelGetter}
                                    label={_('refinement_button_label')}
                                    onChange={(ev) => {
                                        this.handleAddRefinementClick(ev.target.value);
                                    }}
                                    isSecondary
                                />
                            </ButtonWrapper>
                        )}
                        <Button
                            data-testid="toggle-advanced-mode"
                            onClick={this.toggleAdvancedMode}
                        >
                            {_('select_data_advanced_search')}
                        </Button>
                        {resetBtn}
                    </>
                );
            case ADVANCED_MODE:
                return (
                    <>
                        <Button onClick={this.toggleBasicMode}>
                            {_('select_data_basic_search')}
                        </Button>
                        {resetBtn}
                    </>
                );
        }
    };

    render() {
        const {
            dataCollections,
            selectedConditionsDataTypes,
            selectedRefinementFilters,
            selectedConditions,
            queryDataValidationRequest,
            errorInRefinements,
            isLoadingData,
            filtersChangedSinceLastRequest,
            areChartsVisible,
            toggleChartsVisibility,
            hasCdnRestrictions,
            hideActions,
        } = this.props;
        if (!dataCollections || selectedConditionsDataTypes == null) {
            return null;
        }

        const { isCompact } = this.context;

        return (
            <div data-testid="refinement-filters-controls">
                {hasCdnRestrictions && (
                    <StyledAlert color="info" position="middle">
                        {_('refinement_filters_model_restrictions_warning')({
                            Link: LinkToCDN,
                        })}
                    </StyledAlert>
                )}
                {errorInRefinements && (
                    <StyledAlert color="warning" position="middle">
                        {_.get('refinement_filters_refine_selection_warning')}
                    </StyledAlert>
                )}
                <Form onSubmit={(ev) => ev.preventDefault()}>
                    <FlexColumn>{this.renderFiltersControls()}</FlexColumn>

                    {!hideActions && (
                        <ActionsRow isCompact={isCompact}>
                            <ActionsGroup isCompact={isCompact}>
                                {this.renderActionButtons()}
                            </ActionsGroup>
                            <ToggleFormGroup>
                                <ToggleChartsVisibilityButton
                                    {...{ areChartsVisible, toggleChartsVisibility }}
                                />
                            </ToggleFormGroup>
                            <RunQueryButton
                                isLoading={isLoadingData}
                                disabled={
                                    isLoadingData ||
                                    errorInRefinements ||
                                    !filtersChangedSinceLastRequest
                                }
                                onClick={() => {
                                    queryDataValidationRequest({
                                        conditions: selectedConditions,
                                        refinementFilters: selectedRefinementFilters,
                                    });
                                }}
                            />
                        </ActionsRow>
                    )}
                </Form>
            </div>
        );
    }
}

const ConnectedRefinementFiltersControls = compose(
    withStorage,
    connect(mapStateToProps, dispatchProps)
)(RefinementFiltersControls) as FC<RefinementFiltersControlsOwnProps>;

export default ConnectedRefinementFiltersControls;
